/**
 * Utility functions for managing table view preferences in localStorage
 */

export interface ColumnPreferences {
  columnWidths: Record<string, number>;
  columnOrder: string[];
  visibleColumns: string[];
}

const getStorageKey = (entityType: string): string => {
  return `tablePreferences_${entityType}`;
};

/**
 * Get stored column preferences from localStorage
 */
export const getStoredPreferences = (entityType: string): ColumnPreferences | null => {
  try {
    const key = getStorageKey(entityType);
    const stored = localStorage.getItem(key);
    if (!stored) return null;
    return JSON.parse(stored);
  } catch (error) {
    console.error('Error reading preferences from localStorage:', error);
    return null;
  }
};

/**
 * Save column preferences to localStorage
 */
export const savePreferences = (entityType: string, preferences: ColumnPreferences): void => {
  try {
    const key = getStorageKey(entityType);
    localStorage.setItem(key, JSON.stringify(preferences));
  } catch (error) {
    console.error('Error saving preferences to localStorage:', error);
  }
};

/**
 * Clear stored preferences for an entity type
 */
export const clearPreferences = (entityType: string): void => {
  try {
    const key = getStorageKey(entityType);
    localStorage.removeItem(key);
  } catch (error) {
    console.error('Error clearing preferences from localStorage:', error);
  }
};

/**
 * Merge user preferences with defaults
 */
export const mergeWithDefaults = (
  stored: ColumnPreferences | null,
  defaults: ColumnPreferences
): ColumnPreferences => {
  if (!stored) return defaults;

  // Filter out stale keys that no longer exist in the current column config.
  // Without this, a renamed/removed column key in localStorage causes the header
  // to silently drop that cell (find returns undefined → filtered) while the body
  // still renders it, shifting every subsequent column by one position.
  const validKeys = new Set(defaults.columnOrder);

  const filteredOrder = stored.columnOrder.filter((key) => validKeys.has(key));
  // Append any brand-new default columns that aren't in the stored order yet.
  const newKeys = defaults.columnOrder.filter((key) => !filteredOrder.includes(key));
  const finalOrder = filteredOrder.length > 0 ? [...filteredOrder, ...newKeys] : defaults.columnOrder;

  const filteredVisible = stored.visibleColumns.filter((key) => validKeys.has(key));
  const finalVisible = filteredVisible.length > 0 ? filteredVisible : defaults.visibleColumns;

  return {
    columnWidths: { ...defaults.columnWidths, ...stored.columnWidths },
    columnOrder: finalOrder,
    visibleColumns: finalVisible,
  };
};
